import { ReactNode, useEffect, useRef } from 'react';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import {
  SearchModal,
  SearchModalProvider,
  useSearchModal,
} from '@backstage/plugin-search';
import { SearchModalContent } from '../search/SearchModalContent';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

/**
 * The (unpinned) sidebar expands on focus as well as hover. Returning to the
 * browser tab restores focus to the last focused element - often a sidebar
 * link you clicked - so the sidebar popped open on every tab switch. Swallow
 * focus events that arrive right after the window regains focus; deliberate
 * keyboard focus (tabbing while the window is already focused) still expands.
 */
function SidebarFocusGuard({ children }: { children: ReactNode }) {
  const windowFocusedAt = useRef(0);
  useEffect(() => {
    const stamp = () => {
      windowFocusedAt.current = Date.now();
    };
    window.addEventListener('focus', stamp);
    document.addEventListener('visibilitychange', stamp);
    return () => {
      window.removeEventListener('focus', stamp);
      document.removeEventListener('visibilitychange', stamp);
    };
  }, []);
  return (
    <div
      style={{ display: 'contents' }}
      onFocusCapture={event => {
        // Swallow tab-return focus restores, and any focus originating in a
        // portal (the search modal mounts inside the sidebar's React tree, so
        // focusing its input bubbles here and popped the sidebar open behind
        // the dialog).
        if (
          Date.now() - windowFocusedAt.current < 300 ||
          !event.currentTarget.contains(event.target as Node)
        ) {
          event.stopPropagation();
        }
      }}
    >
      {children}
    </div>
  );
}

/**
 * Cmd+K (Ctrl+K on non-Mac) opens the spotlight-style search modal from
 * anywhere. Shares the SearchModalProvider above with SidebarSearchModal -
 * useSearchModal defers to a parent provider when one exists.
 */
/**
 * The search modal rendered OUTSIDE the <Sidebar> tree. SidebarSearchModal
 * mounts the dialog inside the sidebar, and React bubbles portal events
 * through the component tree - so opening the full-screen dialog under the
 * mouse pointer fired mouseenter on the sidebar and popped it open behind
 * the modal (staying open after close).
 */
function GlobalSearchModal() {
  const { state, toggleModal } = useSearchModal();
  return (
    <SearchModal {...state} toggleModal={toggleModal}>
      {({ toggleModal: toggle }) => <SearchModalContent toggleModal={toggle} />}
    </SearchModal>
  );
}

function SearchSidebarItem() {
  const { toggleModal } = useSearchModal();
  return (
    <SidebarItem
      className="search-icon"
      icon={SearchIcon}
      text="Search"
      onClick={toggleModal}
    />
  );
}

function SearchHotkey() {
  const { toggleModal } = useSearchModal();
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'k'
      ) {
        event.preventDefault();
        toggleModal();
      }
    };
    // Capture phase: with the modal open, focus sits in its search input and
    // handlers along the way can swallow the bubbled event - Cmd+K must also
    // CLOSE the modal.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [toggleModal]);
  return null;
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Skipped items
      nav.take('page:search'); // Using search modal instead

      return (
        <SearchModalProvider>
          <SearchHotkey />
          <GlobalSearchModal />
          <SidebarFocusGuard>
            <Sidebar>
              <SidebarLogo />
              <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
                <SearchSidebarItem />
              </SidebarGroup>
              <SidebarDivider />
              <SidebarGroup label="Menu" icon={<MenuIcon />}>
                {nav.take('page:home')}
                {nav.take('page:catalog')}
                {nav.take('page:scaffolder')}
                <SidebarDivider />
                <SidebarScrollWrapper>
                  {nav.rest({ sortBy: 'title' })}
                </SidebarScrollWrapper>
              </SidebarGroup>
              <SidebarSpace />
              <SidebarDivider />
              <NotificationsSidebarItem />
              <SidebarDivider />
              <SidebarGroup
                label="Settings"
                icon={<UserSettingsSignInAvatar />}
                to="/settings"
              >
                {nav.take('page:app-visualizer')}
                {nav.take('page:user-settings')}
              </SidebarGroup>
            </Sidebar>
          </SidebarFocusGuard>
        </SearchModalProvider>
      );
    },
  },
});
